var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getQuestionSetQuestions, removeQuestionFromSet, createQuestionForSet } from '../services/QuestionSetQuestionsService';
// Query Keys
export const QUESTION_SET_QUESTIONS_QUERY_KEYS = {
    all: ['questionSetQuestions'],
    bySet: (questionSetId) => ['questionSetQuestions', 'set', questionSetId]
};
// Hook to fetch questions and options for a specific question set
export function useQuestionSetQuestions(questionSetId) {
    return useQuery({
        queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getQuestionSetQuestions(questionSetId);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || { questions: [], options: { questionTypes: [], frequencyTypes: [], sections: [] } };
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        enabled: !!questionSetId // Only run query if questionSetId is provided
    });
}
// Hook to remove a question from a question set
export function useRemoveQuestionFromSetMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, questionId }) {
            const response = yield removeQuestionFromSet(questionSetId, questionId);
            if (response.error) {
                throw new Error(response.error.message);
            }
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, questionId }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId) });
            // Snapshot the previous value
            const previousQuestions = queryClient.getQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId));
            // Optimistically remove the question
            if (previousQuestions) {
                const filteredQuestions = previousQuestions.questions.filter((question) => question.id !== questionId);
                queryClient.setQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId), Object.assign(Object.assign({}, previousQuestions), { questions: filteredQuestions }));
            }
            // Return a context object with the snapshotted value
            return { previousQuestions, questionSetId };
        }),
        onSuccess: (_data, { questionSetId }) => {
            toast.success('Question removed from question set successfully!');
            // Invalidate related queries
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId)
            });
            // Also invalidate the question sets list to update counts
            void queryClient.invalidateQueries({
                queryKey: ['componentQuestionSets']
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if ((context === null || context === void 0 ? void 0 : context.previousQuestions) && (context === null || context === void 0 ? void 0 : context.questionSetId)) {
                queryClient.setQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(context.questionSetId), Object.assign(Object.assign({}, context.previousQuestions), { questions: context.previousQuestions.questions }));
            }
            toast.error(error.message || 'Failed to remove question from question set. Please try again.');
        },
        onSettled: (_data, _error, { questionSetId }) => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId)
            });
        }
    });
}
// Hook to create a new question and add it to a question set
export function useCreateQuestionForSetMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, data }) {
            const response = yield createQuestionForSet(questionSetId, data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, data }) {
            var _b, _c;
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId) });
            // Snapshot the previous value
            const previousData = queryClient.getQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId));
            // Optimistically add the question
            if (previousData) {
                const optimisticQuestion = {
                    id: Date.now(), // Temporary ID
                    questionText: data.questionText,
                    questionTextEn: data.questionText,
                    questionTextEs: (_b = data.questionTextEs) !== null && _b !== void 0 ? _b : null,
                    questionTextFr: (_c = data.questionTextFr) !== null && _c !== void 0 ? _c : null,
                    questionType: data.questionType,
                    frequencyType: data.frequencyType,
                    section: data.sectionId ? { id: data.sectionId, name: '' } : null,
                    active: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                queryClient.setQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId), Object.assign(Object.assign({}, previousData), { questions: [...previousData.questions, optimisticQuestion] }));
            }
            // Return a context object with the snapshotted value
            return { previousData, questionSetId };
        }),
        onSuccess: (_data, { questionSetId }) => {
            toast.success('Question added to question set successfully!');
            // Invalidate related queries
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId)
            });
            // Also invalidate the question sets list to update counts
            void queryClient.invalidateQueries({
                queryKey: ['componentQuestionSets']
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if ((context === null || context === void 0 ? void 0 : context.previousData) && (context === null || context === void 0 ? void 0 : context.questionSetId)) {
                queryClient.setQueryData(QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(context.questionSetId), context.previousData);
            }
            toast.error(error.message || 'Failed to add question to question set. Please try again.');
        },
        onSettled: (_data, _error, { questionSetId }) => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_QUESTIONS_QUERY_KEYS.bySet(questionSetId)
            });
        }
    });
}
